package com.havn.app.features.clients.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBlack
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListRowSeparator
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextSecondary
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.core.qr.HavnQrCode
import com.havn.app.features.clients.domain.Client
import com.havn.app.features.clients.domain.ClientOpening
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun ClientsScreen(
    viewModel: ClientsViewModel,
    photoPicker: HavnPhotoPicker,
    onOpenOpening: (String) -> Unit = {}
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    HavnPushNavigation(
        targetState = state.mode,
        depthFor = ClientsMode::navigationDepth
    ) { mode ->
        when (mode) {
            ClientsMode.List -> ClientsListHome(state, viewModel)
            ClientsMode.Add -> ClientFormScreen(state, viewModel, isEdit = false)
            ClientsMode.Edit -> ClientFormScreen(state, viewModel, isEdit = true)
            ClientsMode.Detail -> ClientDetailScreen(state, viewModel, photoPicker, onOpenOpening)
        }
    }

    if (state.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = viewModel::dismissDeleteConfirmation,
            title = { Text(strings.deleteClientTitle) },
            text = { Text(strings.deleteClientBody) },
            confirmButton = {
                TextButton(onClick = viewModel::confirmDeleteSelected) {
                    Text(strings.commonDelete, color = HavnDanger)
                }
            },
            dismissButton = {
                TextButton(onClick = viewModel::dismissDeleteConfirmation) {
                    Text(strings.commonCancel)
                }
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ClientsListHome(
    state: ClientsUiState,
    viewModel: ClientsViewModel
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    var gestureStartedAtTop by remember { mutableStateOf(false) }

    HavnPullToRefreshBox(
        isRefreshing = state.isLoading,
        onRefresh = {
            viewModel.refresh()
        },
        modifier = Modifier
            .fillMaxSize()
            .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
    ) {
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 18.dp, vertical = 18.dp),
            verticalArrangement = Arrangement.spacedBy(HavnListDefaults.headerToSearchGap)
        ) {
            item("clients-header") {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.clientsTitle,
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                    IconCircleButton(
                        icon = HavnIconKey.Plus,
                        contentDescription = strings.addClient,
                        onClick = viewModel::startAdd
                    )
                }
            }

            state.message?.let { message ->
                item("clients-message") { InlineMessage(message, theme.primaryAccent) }
            }
            state.error?.let { error ->
                item("clients-error") { InlineMessage(error, Color(0xFFE85D4A)) }
            }

            item("clients-search") {
                HavnSearchField(
                    value = state.searchQuery,
                    onValueChange = viewModel::updateSearchQuery,
                    placeholder = strings.searchClients
                )
            }

            when {
                state.isLoading && state.clients.isEmpty() -> {
                    items(8, key = { "client-skeleton-$it" }) {
                        ClientCardSkeleton()
                    }
                }
                state.clients.isEmpty() -> {
                    item("clients-empty") {
                        EmptyState(
                            title = strings.noClientsYetTitle,
                            body = strings.noClientsYetBody
                        )
                    }
                }
                state.clients.isEmpty() && state.searchQuery.isNotBlank() -> {
                    item("clients-search-empty") {
                        EmptyState(
                            title = strings.noClientsFoundTitle,
                            body = strings.noClientsFoundBody
                        )
                    }
                }
                else -> {
                    items(state.clients, key = { it.id }) { client ->
                        ClientListCard(
                            client = client,
                            onClick = { viewModel.openClient(client.id) }
                        )
                        HavnListRowSeparator()
                    }
                    if (state.hasMoreClients) {
                        item("clients-load-more") {
                            HavnButton(
                                text = "Load more",
                                onClick = viewModel::loadMoreClients,
                                modifier = Modifier.fillMaxWidth(),
                                style = HavnButtonStyle.Secondary,
                                loading = state.isLoadingMore
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ClientCardSkeleton() {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SkeletonBlock(Modifier.size(44.dp).clip(RoundedCornerShape(22.dp)))
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                SkeletonBlock(Modifier.fillMaxWidth(0.46f).height(15.dp))
                SkeletonBlock(Modifier.fillMaxWidth(0.68f).height(12.dp))
            }
            SkeletonBlock(Modifier.width(48.dp).height(20.dp))
        }
    }
}

@Composable
private fun SkeletonBlock(modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = modifier
            .clip(MaterialTheme.shapes.small)
            .background(theme.surfaceElevated.copy(alpha = 0.82f))
    )
}

@Composable
private fun ClientFormScreen(
    state: ClientsUiState,
    viewModel: ClientsViewModel,
    isEdit: Boolean
) {
    val strings = LocalHavnStrings.current
    HavnScreenFrame(
        eyebrow = if (isEdit) strings.clientsTitle else "",
        title = if (isEdit) strings.editClient else strings.newClient,
        subtitle = if (isEdit) strings.clientFormEditSubtitle else strings.clientFormNewSubtitle,
        backLabel = if (isEdit) state.selectedClient?.fullName ?: strings.clientFallback else strings.clientsTitle,
        onBack = if (isEdit) viewModel::cancelForm else viewModel::openList
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            HavnTextField(
                value = state.form.firstName,
                onValueChange = { value -> viewModel.updateForm { copy(firstName = value, validationError = null) } },
                label = strings.firstName,
                leadingIcon = HavnIconKey.User
            )
            HavnTextField(
                value = state.form.lastName,
                onValueChange = { value -> viewModel.updateForm { copy(lastName = value, validationError = null) } },
                label = strings.lastName,
                leadingIcon = HavnIconKey.User
            )
            HavnTextField(
                value = state.form.email,
                onValueChange = { value -> viewModel.updateForm { copy(email = value, validationError = null) } },
                label = strings.email,
                leadingIcon = HavnIconKey.Mail,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
            )
            HavnTextField(
                value = state.form.phone,
                onValueChange = { value -> viewModel.updateForm { copy(phone = value, validationError = null) } },
                label = strings.phone,
                leadingIcon = HavnIconKey.Phone,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone)
            )
            HavnTextField(
                value = state.form.notes,
                onValueChange = { value -> viewModel.updateForm { copy(notes = value) } },
                label = strings.comments,
                singleLine = false,
                leadingIcon = HavnIconKey.Note
            )
            state.form.validationError?.let { InlineMessage(it, Color(0xFFE85D4A)) }
            state.error?.let { InlineMessage(it, Color(0xFFE85D4A)) }
            HavnButton(
                text = if (isEdit) strings.saveClient else strings.createClient,
                onClick = if (isEdit) viewModel::saveEditedClient else viewModel::saveClient,
                modifier = Modifier.fillMaxWidth(),
                loading = state.isSaving,
                leadingIcon = if (isEdit) HavnIconKey.Check else HavnIconKey.Plus
            )
        }
    }
}

@Composable
private fun ClientDetailScreen(
    state: ClientsUiState,
    viewModel: ClientsViewModel,
    photoPicker: HavnPhotoPicker,
    onOpenOpening: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val client = state.selectedClient
    HavnScreenFrame(
        eyebrow = "",
        title = client?.fullName ?: strings.clientFallback,
        subtitle = "",
        backLabel = strings.clientsTitle,
        onBack = viewModel::openList
    ) {
        if (client == null) {
            EmptyState(strings.clientUnavailableTitle, strings.clientUnavailableBody)
            return@HavnScreenFrame
        }
        val greetingName = client.firstName?.takeIf { it.isNotBlank() }
            ?: client.fullName.substringBefore(" ").ifBlank { strings.leadGenericGreetingName }

        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            HavnCard {
                BoxWithConstraints {
                    val wide = maxWidth >= 520.dp
                    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            ClientPhoto(
                                client = client,
                                onClick = { photoPicker.pick(viewModel::uploadClientPhoto) }
                            )
                            Column(
                                modifier = Modifier.weight(1f),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Text(client.fullName, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                                Text(client.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                                Text("${strings.clientPassLabel} ${client.walletPassKey.take(10).uppercase()}", color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
                            }
                            if (wide) {
                                HavnQrCode(
                                    payload = client.qrPayload,
                                    modifier = Modifier.size(116.dp)
                                )
                            }
                        }
                        if (!wide) {
                            Box(
                                modifier = Modifier.fillMaxWidth(),
                                contentAlignment = Alignment.Center
                            ) {
                                HavnQrCode(
                                    payload = client.qrPayload,
                                    modifier = Modifier.size(156.dp)
                                )
                            }
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                            HavnButton(
                                text = strings.addPhoto,
                                onClick = { photoPicker.pick(viewModel::uploadClientPhoto) },
                                modifier = Modifier.weight(1f),
                                style = HavnButtonStyle.Secondary,
                                leadingIcon = HavnIconKey.Plus
                            )
                            HavnButton(
                                text = strings.sendQr,
                                onClick = { viewModel.sendQrCode(client.id) },
                                modifier = Modifier.weight(1f),
                                loading = state.isSaving,
                                leadingIcon = HavnIconKey.Send
                            )
                        }
                    }
                }
            }

            HavnCard {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    ContactActionButton(
                        label = strings.leadActionCall,
                        icon = HavnIconKey.Phone,
                        onClick = { client.phone?.let(HavnExternalActions::openPhone) },
                        modifier = Modifier.weight(1f),
                        enabled = !client.phone.isNullOrBlank()
                    )
                    ContactActionButton(
                        label = strings.leadActionSms,
                        icon = HavnIconKey.Message,
                        onClick = {
                            client.phone?.let {
                                HavnExternalActions.openSms(
                                    to = it,
                                    body = "${strings.greetingHi} $greetingName,"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !client.phone.isNullOrBlank()
                    )
                    ContactActionButton(
                        label = strings.leadActionEmail,
                        icon = HavnIconKey.Mail,
                        onClick = {
                            client.email?.let {
                                HavnExternalActions.openEmail(
                                    to = it,
                                    subject = strings.followUpDefaultSubject,
                                    body = "${strings.greetingHi} $greetingName,"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !client.email.isNullOrBlank()
                    )
                }
            }

            state.message?.let { InlineMessage(it, theme.primaryAccent) }
            state.error?.let { InlineMessage(it, Color(0xFFE85D4A)) }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Text(strings.comments, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                        HavnButton(
                            text = if (state.isEditingComments) strings.commonSave else strings.commonAdd,
                            onClick = { if (state.isEditingComments) viewModel.saveComments() else viewModel.startEditingComments() },
                            style = HavnButtonStyle.Ghost,
                            loading = state.isSaving,
                            leadingIcon = HavnIconKey.Note
                        )
                    }
                    if (state.isEditingComments) {
                        HavnTextField(
                            value = state.commentsDraft,
                            onValueChange = viewModel::updateCommentsDraft,
                            label = strings.comments,
                            singleLine = false,
                            leadingIcon = HavnIconKey.Note
                        )
                    } else {
                        Text(
                            text = client.notes?.takeIf { it.isNotBlank() } ?: strings.noCommentsAdded,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.openingsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                    if (state.isDetailLoading) {
                        LoadingState("Loading openings")
                    } else if (client.openings.isEmpty()) {
                        Text(
                            strings.clientOpeningsEmpty,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    } else {
                        client.openings.forEach { opening ->
                            ClientOpeningRow(
                                opening = opening,
                                onClick = { onOpenOpening(opening.openingId) }
                            )
                        }
                    }
                }
            }

            HavnButton(
                text = strings.commonEdit,
                onClick = viewModel::startEdit,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary
            )
            HavnButton(
                text = strings.commonDelete,
                onClick = viewModel::requestDeleteSelected,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Danger,
                loading = state.isSaving
            )
        }
    }
}

@Composable
private fun ClientListCard(client: Client, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        onClick = onClick,
        color = Color.Transparent,
        modifier = Modifier
            .fillMaxWidth()
            .height(64.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 2.dp, vertical = 7.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            ContactListAvatar(client)
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = client.fullName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = client.email?.takeIf { it.isNotBlank() }
                        ?: client.phone?.takeIf { it.isNotBlank() }
                        ?: strings.clientNoEmail,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            ContactOpeningMetric(
                value = client.openings.size.toString(),
                label = strings.openingsTitle
            )
        }
    }
}

@Composable
private fun ContactOpeningMetric(
    value: String,
    label: String
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(3.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(
                icon = HavnIconKey.Calendar,
                contentDescription = null,
                tint = theme.secondaryText,
                modifier = Modifier.size(14.dp)
            )
            Text(
                value,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        Text(
            label,
            color = theme.mutedText,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun ContactListAvatar(client: Client) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .size(42.dp)
            .clip(RoundedCornerShape(21.dp))
            .background(Brush.linearGradient(listOf(theme.secondaryAccent.copy(alpha = 0.72f), theme.surface)))
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.45f), RoundedCornerShape(21.dp)),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnDatabaseImage(
            dataUrl = client.avatarDataUrl,
            contentDescription = client.fullName,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = client.initials,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun ClientPhoto(
    client: Client,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .size(116.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.background,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            val rendered = HavnDatabaseImage(
                dataUrl = client.avatarDataUrl,
                contentDescription = client.fullName,
                modifier = Modifier.fillMaxSize(),
                contentScale = androidx.compose.ui.layout.ContentScale.Crop
            )
            if (!rendered) {
                Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    HavnIcon(HavnIconKey.User, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(28.dp))
                    Text(strings.photo, color = theme.mutedText, style = MaterialTheme.typography.labelSmall)
                }
            }
        }
    }
}

@Composable
private fun ClientOpeningRow(
    opening: ClientOpening,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier.padding(10.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(74.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .background(theme.card),
                contentAlignment = Alignment.Center
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = opening.propertyPhotoDataUrl,
                    contentDescription = opening.propertyTitle,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
                }
            }
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                Text(opening.displayTitle, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(opening.propertyAddress, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(opening.startsAt.take(10), color = theme.mutedText, style = MaterialTheme.typography.labelMedium)
            }
            Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(7.dp)) {
                HavnStatusBadge(status = opening.temperature.toTemperatureStatus())
                HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            }
        }
    }
}

private fun String.toTemperatureStatus(): HavnStatus = when (lowercase()) {
    "hot" -> HavnStatus.Hot
    "cold" -> HavnStatus.Cold
    else -> HavnStatus.Warm
}

@Composable
private fun IconCircleButton(
    icon: HavnIconKey,
    contentDescription: String,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .size(42.dp)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            HavnIcon(icon, contentDescription = contentDescription, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
        }
    }
}

private val Client.initials: String
    get() = fullName
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }

@Composable
private fun ContactActionButton(
    label: String,
    icon: HavnIconKey,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    val contentColor = if (enabled) theme.primaryAccent else theme.mutedText
    Surface(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier.height(64.dp),
        shape = androidx.compose.foundation.shape.RoundedCornerShape(16.dp),
        color = theme.surface.copy(alpha = if (enabled) 0.74f else 0.42f),
        border = BorderStroke(1.dp, if (enabled) theme.primaryAccent.copy(alpha = 0.45f) else theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(icon, contentDescription = label, tint = contentColor, modifier = Modifier.size(19.dp))
            Spacer(Modifier.width(7.dp))
            Text(
                label,
                color = if (enabled) theme.primaryText else theme.mutedText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Clip
            )
        }
    }
}

@Composable
private fun InlineMessage(text: String, color: Color) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = color.copy(alpha = 0.12f),
        border = BorderStroke(1.dp, color.copy(alpha = 0.42f))
    ) {
        Text(
            text = text,
            color = color,
            style = MaterialTheme.typography.bodyMedium,
            modifier = Modifier.padding(12.dp)
        )
    }
}

private fun ClientsMode.navigationDepth(): Int = when (this) {
    ClientsMode.List -> 0
    ClientsMode.Add,
    ClientsMode.Edit,
    ClientsMode.Detail -> 1
}
